import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { axiosApi } from "./axios";
import {
  GetNotificationsByExpeditionIdResponseDto,
  GetNotificationsDto,
  GetNotificationsResponseDto,
} from "@/types/api/api-types";
import { AxiosResponse } from "axios";

//#region Get

const getNotificationsBo = async (payload: GetNotificationsDto) => {
  return axiosApi
    .get<GetNotificationsResponseDto>(`v1/notifications/bo`, {
      params: payload,
    })
    .then((res) => res.data);
};

const NOTIFICATIONS_PAGE_SIZE = 25;

type InfiniteNotificationsParams = {
  logKeys?: string;
  includeRead?: boolean;
};

export const useGetInfiniteNotificationsBo = (params: InfiniteNotificationsParams = {}) => {
  return useInfiniteQuery({
    queryKey: ["notifications", "bo", "infinite", params],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      getNotificationsBo({ take: NOTIFICATIONS_PAGE_SIZE, skip: pageParam, ...params }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.notifications.length < NOTIFICATIONS_PAGE_SIZE) return undefined;
      return lastPageParam + NOTIFICATIONS_PAGE_SIZE;
    },
  });
};

type NotificationsCountParams = { logKeys?: string; urgentLogKeys?: string };
export type NotificationsCountResult = { normal: number; urgent: number };

const getNotificationsCountBo = async (params: NotificationsCountParams) => {
  return axiosApi
    .get<NotificationsCountResult>(`v1/notifications/bo/count`, { params })
    .then((res) => res.data);
};

export const useGetNotificationsCountBo = (params: NotificationsCountParams = {}) => {
  return useQuery({
    queryKey: ["notifications", "bo", "count", params],
    queryFn: () => getNotificationsCountBo(params),
    refetchInterval: 1000 * 60,
  });
};

const getNotificationsByExpeditionIdBo = async (expeditionId: string) => {
  return axiosApi
    .get<GetNotificationsByExpeditionIdResponseDto>(
      `v1/notifications/bo/expedition/${expeditionId}`,
    )
    .then((res) => res.data);
};

export const useGetNotificationsByExpeditionIdBo = (
  expeditionId: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ["notifications", "bo", "expedition", expeditionId],
    queryFn: () => getNotificationsByExpeditionIdBo(expeditionId),
    enabled: !!expeditionId && (options?.enabled ?? true),
  });
};

//#endregion

//#region Patch

const toggleNotificationReadBo = async ({ id, read }: { id: string; read: boolean }) => {
  return axiosApi.patch<{ expeditionId: string }>(`v1/notifications/bo/${id}`, { read });
};

export const useToggleNotificationReadBo = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boParameters");
  return useMutation({
    mutationFn: toggleNotificationReadBo,
    onSuccess: (res: AxiosResponse<{ expeditionId: string }>) => {
      queryClient.invalidateQueries({ queryKey: ["expedition", "all", "bo"] });
      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", res.data.expeditionId] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "bo"] });
      queryClient.invalidateQueries({
        queryKey: ["notifications", "bo", "expedition", res.data.expeditionId],
      });
    },
    onError: () => {
      toast.error(t("notificationsApi.toggleReadError"));
    },
  });
};

//#endregion
